import { useMemo, useState } from 'react';
import type { Holiday, HolidayKind } from '@forecastify/shared';
import { TextInput } from '@forecastify/ui';
import { useI18n, useT, type Translate } from '../../i18n';
import { EditorDialog } from './editor-dialog';
import { DateField } from '../date-field';
import { SettingsPage } from './settings-page';
import { SettingsTable, type SettingsRow } from './settings-table';
import { Field } from '../card';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Beállítások → Ünnepnapok. C42, the workspace calendar.                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THIS IS THE ORIGINAL TABLE, WIDENED — AND THE `DataList` REBUILD THAT STOOD HERE FOR A DAY
 * IS REVERTED.** The ask was one sentence: *"a wider table so everything could fit"*. It was
 * answered with the shared list component, which brought a search box, saved views, filter chips
 * and a filter that arrived pre-set to the current year — *"prefilled with a 2026 filter that's a
 * hassle to change"*. The year is a PARAMETER of this screen, not a filter of its rows: it decides
 * which rows are fetched at all, so wearing a chip that looks removable was a lie about what it
 * does.
 *
 * ⚠️ **What survived the revert is the one thing that was not chrome:** a synchronised row cannot be
 * edited (the server refuses it too), and the year list now reaches back to the workspace's own
 * first year. Everything else is exactly what was here before.
 *
 * ⚠️ **The width fix is the width fix**: the card lost its 760px cap and the table its horizontal
 * scroll, so the three filters and the two buttons share one row because there is room for them.
 *
 * Mockup: the `isWSHolidays` block — a year picker, a name search, a kind filter, the import button
 * and *Nap hozzáadása* over a six-column table.
 *
 * ⚠️⚠️ **THE FOOTER LINE IS THE WHOLE POINT OF THE SCREEN AND IS NOT DECORATION.** *"N ünnepnap · M
 * munkaszüneti · K áthelyezett munkanap"* is the only place the third kind is visible as a
 * category, and the third kind is the one everybody gets wrong: an *áthelyezett munkanap* **ADDS** a
 * working day. `decisions.md` P3-3.
 *
 * ⚠️ **The source badge is what makes the import safe to press.** A person can see which rows the
 * API owns and which are theirs — and theirs survive, which is the rule the note under the table
 * states in words rather than leaving to be discovered.
 */

/** The mockup's own colours, per kind — red, amber, teal — as the status tokens they match. */
const KIND_DOT: Readonly<Record<HolidayKind, string>> = {
  public: 'var(--status-bad)',
  rest: 'var(--status-warn)',
  workday: 'var(--status-ok)',
};

export function kindLabel(t: Translate, kind: HolidayKind): string {
  /* ⚠️ One `t()` per branch with a LITERAL key — the i18n rule. A computed key is invisible to the
     catalogue gate and to anybody grepping for a string. */
  if (kind === 'public') return t('settings.holidays.kind.public');
  if (kind === 'rest') return t('settings.holidays.kind.rest');
  return t('settings.holidays.kind.workday');
}

export interface HolidayDraft {
  readonly id: string | null;
  readonly onDate: string;
  readonly name: string;
  readonly kind: HolidayKind;
  readonly version: number;
}

export function HolidaysSection(props: {
  /** Until the first answer: skeleton rows and no figures. */
  readonly loading?: boolean;
  readonly year: number;
  readonly years: readonly number[];
  readonly holidays: readonly Holiday[];
  readonly countryLabel: string;
  readonly canManage: boolean;
  readonly importing: boolean;
  readonly serverError: string | null;
  readonly onYear: (year: number) => void;
  readonly onImport: () => void;
  readonly onSave: (draft: HolidayDraft) => Promise<boolean>;
  readonly onDelete: (holiday: Holiday) => void;
}) {
  const t = useT();
  /* 26 Oct 2026 · a save in flight, and whether the last one was refused — the dialog stays
     open until it lands, and shows its refusal inside. */
  const [saving, setSaving] = useState(false);
  const [saveFailed, setSaveFailed] = useState(false);
  const { locale } = useI18n();
  const [query, setQuery] = useState('');
  const [kind, setKind] = useState<HolidayKind | ''>('');
  const [draft, setDraft] = useState<HolidayDraft | null>(null);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return props.holidays.filter((holiday) => {
      if (kind !== '' && holiday.kind !== kind) return false;
      return needle === '' || holiday.name.toLowerCase().includes(needle);
    });
  }, [props.holidays, query, kind]);

  const rows: SettingsRow[] = shown.map((holiday) => ({
    id: holiday.id,
    onOpen:
      !props.canManage || !editable(holiday)
        ? undefined
        : () => {
            setDraft({
              id: holiday.id,
              onDate: holiday.onDate,
              name: holiday.name,
              kind: holiday.kind,
              version: holiday.version,
            });
          },
    cells: [
      { kind: 'mono' as const, text: holiday.onDate },
      /* \u26a0\u26a0 **THE WEEKDAY, WHICH IS THE COLUMN THAT MAKES THE THIRD KIND LEGIBLE.** An
         *\u00e1thelyezett munkanap* is a SATURDAY somebody has to work; without the day name the
         reader has to hold a calendar in their head to see why the row is unusual. */
      {
        kind: 'text' as const,
        text: shortDay(holiday.onDate, locale),
        className: 'text-muted-foreground',
      },
      { kind: 'name' as const, text: holiday.name },
      { kind: 'dot' as const, text: kindLabel(t, holiday.kind), color: KIND_DOT[holiday.kind] },
      {
        kind: 'badge' as const,
        text:
          holiday.source === 'nager'
            ? t('settings.holidays.source.nager')
            : t('settings.holidays.source.custom'),
        tone: holiday.source === 'nager' ? ('secondary' as const) : ('outline' as const),
      },
      !props.canManage
        ? { kind: 'blank' as const }
        : {
            kind: 'kebab' as const,
            ariaLabel: t('settings.holidays.rowMenu', { name: holiday.name }),
            items: [
              /* \u26a0\u26a0 **A SYNCHRONISED DAY IS NOT EDITABLE, AND THE SERVER REFUSES IT TOO.**
                 Editing a `nager` row used to turn it `custom` so the next import could not undo
                 it \u2014 coherent, and it produced a row presented as the national calendar's while
                 saying something the calendar does not.
                 \u26a0\ufe0f Deleting stays: *"we work that day"* is the workspace's decision. */
              ...(editable(holiday)
                ? [
                    {
                      label: t('common.edit'),
                      icon: 'pencil' as const,
                      onSelect: () => {
                        setDraft({
                          id: holiday.id,
                          onDate: holiday.onDate,
                          name: holiday.name,
                          kind: holiday.kind,
                          version: holiday.version,
                        });
                      },
                    },
                  ]
                : []),
              {
                label: t('common.delete'),
                icon: 'trash' as const,
                destructive: true,
                onSelect: () => {
                  props.onDelete(holiday);
                },
              },
            ],
          },
    ],
  }));

  return (
    <div data-screen="settings-holidays" className="flex min-h-0 flex-1 flex-col">
      <SettingsPage
        title={t('settings.nav.holidays')}
        secondaryAction={
          !props.canManage
            ? undefined
            : {
                label: props.importing
                  ? t('settings.holidays.importing')
                  : t('settings.holidays.import'),
                icon: 'refresh-cw',
                disabled: props.importing,
                onSelect: props.onImport,
              }
        }
        action={
          !props.canManage
            ? undefined
            : {
                label: t('settings.holidays.add'),
                icon: 'plus',
                onSelect: () => {
                  /* \u26a0\ufe0f `rest` by default, which is the mockup's own choice and the right
                     one: the `public` days arrive from the import, so a day somebody adds by hand
                     is nearly always a company day off. */
                  setDraft({
                    id: null,
                    onDate: `${String(props.year)}-01-01`,
                    name: '',
                    kind: 'rest',
                    version: 0,
                  });
                },
              }
        }
        error={props.serverError}
        search={query}
        onSearch={setQuery}
        searchPlaceholder={t('settings.holidays.search')}
        filters={[
          /**
           * \u26a0\u26a0 **THE YEAR IS A PARAMETER, NOT A FILTER \u2014 AND IT IS STILL DRAWN AS ONE
           * HERE, WHICH IS FINE NOW.** The v3 objection was a removable CHIP that looked like it
           * could be cleared back to *all years*; this strip is a row of plain selects with no
           * clear affordance, and the year select has no empty option at all. It decides which
           * rows are fetched and it says so by never offering *minden*.
           */
          {
            key: 'year',
            value: String(props.year),
            ariaLabel: t('settings.holidays.year'),
            options: props.years.map((year) => ({ value: String(year), label: String(year) })),
            onChange: (value) => {
              if (value !== '') props.onYear(Number(value));
            },
          },
          {
            key: 'kind',
            value: kind,
            ariaLabel: t('settings.holidays.kind'),
            options: [
              { value: '', label: t('settings.holidays.kind.all') },
              { value: 'public', label: t('settings.holidays.kind.public') },
              { value: 'rest', label: t('settings.holidays.kind.rest') },
              { value: 'workday', label: t('settings.holidays.kind.workday') },
            ],
            onChange: (value) => {
              setKind(value === 'public' || value === 'rest' || value === 'workday' ? value : '');
            },
          },
        ]}
      >
        <SettingsTable
          grid="118px 96px minmax(0,1fr) 190px 128px 36px"
          minWidth={900}
          columns={[
            { label: t('settings.holidays.column.date') },
            { label: t('settings.holidays.column.day') },
            { label: t('settings.holidays.column.name') },
            { label: t('settings.holidays.column.kind') },
            { label: t('settings.holidays.column.source') },
            {},
          ]}
          groups={[{ rows }]}
          loading={props.loading}
          empty={{
            icon: 'calendar-days',
            title: t('settings.holidays.empty'),
            body: t('settings.holidays.emptyBody'),
          }}
        />
      </SettingsPage>

      <EditorDialog
        open={draft !== null}
        title={
          draft?.id === null
            ? t('settings.holidays.dialog.new')
            : t('settings.holidays.dialog.edit')
        }
        saveDisabled={draft === null || draft.name.trim() === ''}
        onClose={() => {
          setSaveFailed(false);
          setDraft(null);
        }}
        busy={saving}
        error={saveFailed ? props.serverError : null}
        onSave={() => {
          if (draft === null) return;
          setSaving(true);
          void props.onSave(draft).then((landed) => {
            setSaving(false);
            setSaveFailed(!landed);
            if (landed) setDraft(null);
          });
        }}
      >
        {draft === null ? null : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('settings.holidays.column.date')} required>
                <DateField
                  label={t('settings.holidays.column.date')}
                  value={draft.onDate}
                  clearable={false}
                  onChange={(next) => {
                    if (next !== '') setDraft({ ...draft, onDate: next });
                  }}
                />
              </Field>
              <Field label={t('settings.holidays.column.name')} required>
                <TextInput
                  value={draft.name}
                  placeholder={t('settings.holidays.namePlaceholder')}
                  onChange={(event) => {
                    setDraft({ ...draft, name: event.target.value });
                  }}
                />
              </Field>
            </div>

            {/**
             * ⚠️⚠️ **RADIO CARDS WITH THE COLOUR DOT, NOT A DROPDOWN.** The three kinds are the one
             * thing on this screen a reader gets wrong, and the difference between them is a
             * sentence rather than a name: two of them REMOVE a working day and the third ADDS one.
             * A select shows one label at a time and hides the sentence that matters.
             *
             * ⚠️ **Real radios**, so arrow keys move between them and the group is announced once.
             */}
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-[12px] font-medium">
                {t('settings.holidays.column.kind')}
              </legend>
              {KINDS.map((option) => (
                <label
                  key={option}
                  className={`flex cursor-pointer items-start gap-2.5 rounded-lg border p-2.5 transition-colors ${
                    draft.kind === option ? 'border-primary bg-primary/5' : 'hover:bg-accent/50'
                  }`}
                >
                  <input
                    type="radio"
                    className="accent-primary mt-0.5 size-4 shrink-0"
                    checked={draft.kind === option}
                    onChange={() => {
                      setDraft({ ...draft, kind: option });
                    }}
                  />
                  <span
                    aria-hidden
                    className="mt-[5px] size-2 shrink-0 rounded-full"
                    style={{ background: KIND_DOT[option] }}
                  />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-[12.5px] font-medium">{kindLabel(t, option)}</span>
                    <span className="text-muted-foreground text-[11.5px]">
                      {kindHint(t, option)}
                    </span>
                  </span>
                </label>
              ))}
            </fieldset>
          </>
        )}
      </EditorDialog>
    </div>
  );
}

/**
 * ⚠️ Whether this row is the workspace's own. A `nager` row belongs to the national calendar, and
 * `holiday.service.ts` refuses a write to one with `conflict` — nobody may do it, so a permission
 * code would send an admin looking for a role that would let them.
 */
function editable(holiday: Holiday): boolean {
  return holiday.source !== 'nager';
}

const KINDS = ['public', 'rest', 'workday'] as const;

/** ⚠️ One `t()` per branch with a LITERAL key — the same rule `kindLabel` follows. */
function kindHint(t: Translate, kind: HolidayKind): string {
  if (kind === 'public') return t('settings.holidays.hint.public');
  if (kind === 'rest') return t('settings.holidays.hint.rest');
  return t('settings.holidays.hint.workday');
}

/**
 * The weekday of a wire date, short, in the app's locale.
 *
 * ⚠️ **`T00:00:00Z` and a UTC format** — a bare `new Date('2026-01-01')` is midnight UTC rendered in
 * the viewer's zone, which is 31 December west of Greenwich. The driver rows in `hard-won.md` carry
 * the longer version of why a calendar date and an instant never share a converter.
 */
function shortDay(onDate: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(
    new Date(`${onDate}T00:00:00Z`),
  );
}
